import React, { FC, Fragment, useEffect, useState } from 'react';

import { DataGrid, GridCellParams } from '@mui/x-data-grid';
import { LinearProgress } from '@mui/material';

import styles from './PortfoliosPage.module.scss';
import { PortfoliosPageProps } from './PortfoliosPageProps';
import { PortfolioModel } from './PortfolioModel';
import { PortfolioService } from './PortfolioService';
import { PortfoliosCustomToolbar, PortfoliosCustomToolbarProps } from './PortfolioTable/PortfoliosCustomToolbar';
import { portfolioColumns } from './PortfolioTable/PortfolioTableDefinition';
import { dataGridFocusStyles } from 'styles';
import CreatePortfolioDialog from './CreatePortfolioDialog/CreatePortfolioDialog';
import PortfolioDetailsDialog from './PortfolioDetailsDialog/PortfolioDetailsDialog';
import { AlertConfigModel } from 'dialogs/AlertDialog/AlertConfigModel';
import AlertDialog from 'dialogs/AlertDialog/AlertDialog';


export const PortfoliosPage: FC<PortfoliosPageProps> = () => {

  const [currentAlert, setCurrentAlert] = useState<AlertConfigModel | null>(null);
  const [displayedPortfolios, setDisplayedPortfolios] = useState<PortfolioModel[]>([])
  const [isAlertDialogOpen, setIsAlertDialogOpen] = useState<boolean>(false);
  const [isCreatePortfolioDialogOpen, setIsCreatePortfolioDialogOpen] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isPortfolioDetailsDialogOpen, setIsPortfolioDetailsDialogOpen] = useState<boolean>(false)
  const [portfolios, setPortfolios] = useState<PortfolioModel[]>([]);
  const [selectedPortfolioId, setSelectedPortfolioId] = useState<string>('');
  const [showAdminPortfolios, setShowAdminPortfolios] = useState<boolean>(true);
  const [showPersonalPortfolios, setShowPersonalPortfolios] = useState<boolean>(true);

  useEffect(() => {
    getPortfolios();
  }, []);

  useEffect(() => {
    setIsLoading(true);
    if (showAdminPortfolios && showPersonalPortfolios) {
      setDisplayedPortfolios(portfolios);
    } else if (showAdminPortfolios) {
      const adminPortfolios = portfolios.filter(portfolio => portfolio.type === 'adminCreated');
      setDisplayedPortfolios(adminPortfolios);
    } else if (showPersonalPortfolios) {
      const personalPortfolios = portfolios.filter(portfolio => portfolio.type === 'personal');
      setDisplayedPortfolios(personalPortfolios);
    } else {
      setDisplayedPortfolios([])
    }
    setIsLoading(false);
  }, [portfolios, showAdminPortfolios, showPersonalPortfolios]);

  const getPortfolios = async (): Promise<void> => {
    setIsLoading(true);

    try {
      const fetchedPortfolios = await PortfolioService.getPortfolios();

      if (!fetchedPortfolios) {
        throw new Error('No portfolios returned by the service');
      }

      setPortfolios(fetchedPortfolios);
    } catch (error) {
      console.error('Failed getting portfolios:', error);
      setPortfolios([]);
      const alertConfig: AlertConfigModel = {title: 'Error', message: 'Failed at getting portfolios. Please refresh, and contact support if the issue persists.'};
      setCurrentAlert(alertConfig);
      setIsAlertDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  }

  const cellClickHandler = (params: GridCellParams): void => {
    if (params.field === 'portfolioDetails') {
      setSelectedPortfolioId(params.id as string);
      setIsPortfolioDetailsDialogOpen(true);
    }
  }

  const createPortfolioHandler = (): void => {
    setIsCreatePortfolioDialogOpen(true);
  };

  const toggleAdminPortfoliosHandler = (): void => {
    setShowAdminPortfolios(prevState => !prevState);
  };

  const togglePersonalPortfoliosHandler = (): void => {
    setShowPersonalPortfolios(prevState => !prevState);
  };

  const portfolioCreationHandler = async (): Promise<void> => {
    await getPortfolios();
  };

  const portfolioUpdateHandler = async (): Promise<void> => {
    await getPortfolios();
  };

  const portfolioDetailsDialogCloseHandler = (): void => {
    setIsPortfolioDetailsDialogOpen(false);
    setSelectedPortfolioId('');
  };

  const portfoliosToolbarProps: PortfoliosCustomToolbarProps = {
    onCreateClick: createPortfolioHandler,
    switches: {
      adminPortfolios: {
        isChecked: showAdminPortfolios,
        onToggle: toggleAdminPortfoliosHandler
      },
      personalPortfolios: {
        isChecked: showPersonalPortfolios,
        onToggle: togglePersonalPortfoliosHandler
      }
    }
  }

  return (
    <Fragment>
      { isCreatePortfolioDialogOpen &&
        <CreatePortfolioDialog
          setOpen={setIsCreatePortfolioDialogOpen}
          onPortfolioCreation={portfolioCreationHandler}
        />
      }

      { isPortfolioDetailsDialogOpen &&
        <PortfolioDetailsDialog
          onClose={portfolioDetailsDialogCloseHandler}
          portfolioId={selectedPortfolioId}
          onPortfolioUpdate={portfolioUpdateHandler}
        />
      }

      <div
        className={styles.PortfoliosPage}
        data-testid="PortfoliosPage"
      >
        <DataGrid
          rows={displayedPortfolios}
          columns={portfolioColumns}
          slots={{
            loadingOverlay: LinearProgress,
            toolbar: () => PortfoliosCustomToolbar(portfoliosToolbarProps),
          }}
          loading={isLoading}
          className="project-data-grid"
          getRowId={row => row._id}
          sx={dataGridFocusStyles}
          onCellClick={(params, event, details) => cellClickHandler(params)}
        />
      </div>

      { isAlertDialogOpen && currentAlert &&
        <AlertDialog
          onClose={() => {
            setIsAlertDialogOpen(false);
            setCurrentAlert(null);
          }}
          title={currentAlert.title}
          message={currentAlert.message}
        ></AlertDialog>
      }
    </Fragment>
  );
};
